import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';

import { ReactComponent as StarFilled } from '../../assets/stars/star-fill.svg';
import { ReactComponent as StarLine } from '../../assets/stars/star-line.svg';
import { labels as defaultLabels, buttonColorMap } from './materialExports';

const STAR_COUNT = 5;
const STAR_SIZE = 32;
const STAR_GAP = 8;

const RatingStars = ({
  rating,
  hoverValue,
  onSelect,
  onHover,
  onHoverEnd,
  labelsMap,
}) => {
  const effectiveLabels = labelsMap || defaultLabels;
  const clampedRating = Math.max(0, Math.min(rating, STAR_COUNT));
  const effectiveHover = Math.max(0, Math.min(hoverValue || 0, STAR_COUNT));
  const displayValue = effectiveHover > 0 ? effectiveHover : clampedRating;
  const label = displayValue > 0 ? effectiveLabels[displayValue] : undefined;
  const labelColor = buttonColorMap[displayValue] || '#3c3c3c';
  const isHovering = effectiveHover > 0 && effectiveHover !== clampedRating;

  const handleMouseLeave = () => {
    if (onHoverEnd) {
      onHoverEnd();
    }
  };

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={`${STAR_GAP}px`}
      width="100%"
      onMouseLeave={handleMouseLeave}
    >
      {Array.from({ length: STAR_COUNT }).map((_, index) => {
        const value = index + 1;
        const StarIcon = value <= clampedRating ? StarFilled : StarLine;
        const showLabel = displayValue === value;
        const isCurrentHover = isHovering && value === effectiveHover;
        return (
          <Box
            key={`star-${value}`}
            display="flex"
            flexDirection="column"
            alignItems="center"
            flex={1}
          >
            <ButtonBase
              disableRipple
              focusRipple={false}
              onClick={() => onSelect && onSelect(value)}
              onMouseEnter={() => onHover && onHover(value)}
              sx={{
                width: STAR_SIZE,
                height: STAR_SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                transition: 'transform 0.2s ease, opacity 0.2s ease',
                transform: isCurrentHover ? 'scale(1.15)' : 'scale(1)',
                opacity: 1,
                '&:hover': {
                  transform: 'scale(1.15)',
                },
              }}
            >
              <StarIcon style={{ width: '100%', height: '100%' }} />
            </ButtonBase>
            {showLabel && label && (
              <Typography
                variant="caption"
                sx={{
                  marginTop: '4px',
                  color: labelColor,
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '11px',
                  textAlign: 'center',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
              >
                {label}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

RatingStars.propTypes = {
  rating: PropTypes.number,
  hoverValue: PropTypes.number,
  onSelect: PropTypes.func,
  onHover: PropTypes.func,
  onHoverEnd: PropTypes.func,
  labelsMap: PropTypes.objectOf(PropTypes.string),
};

RatingStars.defaultProps = {
  rating: 0,
  hoverValue: 0,
  onSelect: undefined,
  onHover: undefined,
  onHoverEnd: undefined,
  labelsMap: undefined,
};

export default RatingStars;
